/**
 * Recordings this browser requested a transcription for, per case, kept in `localStorage` to survive a reload.
 * The BE doesn't report transcription status yet; replace `readTranscribingFiles` with it once it does.
 */

/** Document id -> ms timestamp the transcription was requested at. */
export type TranscribingFiles = Record<string, number>;

export const EMPTY_TRANSCRIBING_FILES: TranscribingFiles = {};

const STORAGE_KEY_PREFIX = 'fletch:transcribing:';

export const transcribingFilesStorageKey = (caseId: string) => `${STORAGE_KEY_PREFIX}${caseId}`;

/**
 * How long a request counts as running. There's no completion signal yet, so this keeps a failed
 * request from showing "Transcribing" forever.
 */
export const TRANSCRIBING_ENTRY_TTL_MS = 6 * 60 * 60 * 1000;

const parseEntries = (value: unknown, now: number): TranscribingFiles => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return EMPTY_TRANSCRIBING_FILES;

  return Object.entries(value).reduce<TranscribingFiles>((acc, [documentId, requestedAt]) => {
    // A timestamp from the future is as unusable as a stale one — both mean the entry
    // cannot be aged, so neither is kept.
    if (typeof requestedAt !== 'number' || !Number.isFinite(requestedAt)) return acc;
    if (requestedAt > now || now - requestedAt > TRANSCRIBING_ENTRY_TTL_MS) return acc;

    acc[documentId] = requestedAt;
    return acc;
  }, {});
};

/**
 * Never throws: a full or unavailable `localStorage` (private browsing, a blocked
 * origin) has to leave the case page working, just without remembering the request.
 */
export const readTranscribingFiles = (caseId: string, now: number = Date.now()): TranscribingFiles => {
  try {
    const raw = localStorage.getItem(transcribingFilesStorageKey(caseId));
    if (!raw) return EMPTY_TRANSCRIBING_FILES;

    return parseEntries(JSON.parse(raw), now);
  } catch {
    return EMPTY_TRANSCRIBING_FILES;
  }
};

/** Requests for every case, by case id. Cases with none are left out. */
export const readAllTranscribingFiles = (now: number = Date.now()): Record<string, TranscribingFiles> => {
  const filesByCaseId: Record<string, TranscribingFiles> = {};
  try {
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      const caseId = key.slice(STORAGE_KEY_PREFIX.length);
      const files = readTranscribingFiles(caseId, now);
      if (Object.keys(files).length > 0) filesByCaseId[caseId] = files;
    }
  } catch {
    // See `readTranscribingFiles`.
  }
  return filesByCaseId;
};

export const writeTranscribingFiles = (caseId: string, files: TranscribingFiles) => {
  try {
    const key = transcribingFilesStorageKey(caseId);
    // Nothing transcribing is the default state, so it is dropped rather than stored.
    if (Object.keys(files).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(files));
  } catch {
    // See `readTranscribingFiles`.
  }
};

/** Whether a row's recording is currently being transcribed. */
export const isFileTranscribing = (files: TranscribingFiles, documentId: string | null | undefined): boolean =>
  !!documentId && files[documentId] !== undefined;
